import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { YearStatus } from "@/components/YearSelector/YearSelector";
import { ImpactDataset } from "@/hooks/useEcoInventDatasets";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

export interface GoodsAndServicesItem {
  id: number;
  name: string;
  impact_dataset: ImpactDataset | null;
  status: "complete" | "incomplete";
  deletable: boolean;
  facility_relations: number;
}

export function useGetGoodsAndServicesItems({
  pagination,
  filter,
  search,
  sort,
  enabled,
  showUnrelated = false,
  year,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
  showUnrelated?: boolean;
  year?: number;
}) {
  return useQuery({
    queryKey: ["goods_and_services", "list", pagination, filter, search, sort, year, showUnrelated],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<GoodsAndServicesItem>>("/goods_and_services", {
          params: {
            ...(year ? { year } : {}),
            show_unrelated: showUnrelated,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then((result) => result.data),
  });
}

export function useGetGoodsAndServicesItem({
  goodsAndServicesItemId,
  enabled,
}: {
  goodsAndServicesItemId: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["goods_and_services", "detail", goodsAndServicesItemId, "details"],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GoodsAndServicesItem>("/goods_and_services/" + goodsAndServicesItemId)
        .then((result) => result.data),
  });
}

export interface GoodsAndServicesItemUnit {
  start_unit: string;
  target_unit: string;
  factor: number | null;
  status: "complete" | "incomplete" | "validating";
}

export type GetGoodsAndServicesItemUnits = GoodsAndServicesItemUnit[];

export function useGetGoodsAndServicesItemUnits({
  goodsAndServicesItemId,
}: {
  goodsAndServicesItemId: number;
}) {
  return useQuery({
    queryKey: ["goods_and_services", "detail", goodsAndServicesItemId, "units"],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<GetGoodsAndServicesItemUnits>(
          "/goods_and_services/" + goodsAndServicesItemId + "/units"
        )
        .then((result) => result.data),
  });
}

export interface UpdateGoodsAndServicesItemUnit {
  start_unit: string;
  target_unit: string;
  factor: number | null;
}

export function useUpdateGoodsAndServicesItemUnit() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({
      goodsAndServicesItemId,
      goodsAndServicesItemUnit,
    }: {
      goodsAndServicesItemId: number;
      goodsAndServicesItemUnit: UpdateGoodsAndServicesItemUnit;
    }) =>
      axios.post<{
        unit_status: "complete" | "incomplete";
        goodsAndServicesItem: GoodsAndServicesItem;
      }>("/goods_and_services/" + goodsAndServicesItemId + "/units", goodsAndServicesItemUnit),
    onSuccess: (
      { data: { unit_status: newStatus, goodsAndServicesItem } },
      { goodsAndServicesItemId, goodsAndServicesItemUnit }
    ) => {
      smartQueryClient.invalidateQueries(["dashboard"]);
      smartQueryClient.invalidateQueries(["goods_and_services", "list"]);
      smartQueryClient.invalidateQueries([
        "goods_and_services",
        "detail",
        goodsAndServicesItemId,
        "details",
      ]);

      smartQueryClient.setQueryData<GetGoodsAndServicesItemUnits>(
        ["goods_and_services", "detail", goodsAndServicesItemId, "units"],
        (goodsAndServicesItemUnits) => {
          return goodsAndServicesItemUnits
            ? goodsAndServicesItemUnits.map((unit) => {
                if (
                  unit.start_unit === goodsAndServicesItemUnit.start_unit &&
                  unit.target_unit === goodsAndServicesItemUnit.target_unit
                ) {
                  return {
                    ...unit,
                    status: newStatus,
                  };
                } else {
                  return unit;
                }
              })
            : undefined;
        }
      );

      smartQueryClient.setQueryData(
        ["goods_and_services", "detail", goodsAndServicesItemId, "details"],
        goodsAndServicesItem
      );
    },
  });
}

export interface GoodsAndServicesItemPatch {
  name?: string;
  impact_dataset_id?: number;
  clear?: "impact_dataset_id"[];
}

export function usePatchGoodsAndServicesItem() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({
      goodsAndServicesItemId,
      patch,
    }: {
      goodsAndServicesItemId: number;
      patch: GoodsAndServicesItemPatch;
    }) => axios.patch<GoodsAndServicesItem>("/goods_and_services/" + goodsAndServicesItemId, patch),
    onSuccess: ({ data: goodsAndServicesItem }, { goodsAndServicesItemId }) => {
      smartQueryClient.invalidateQueries(["goods_and_services", "list"]);
      smartQueryClient.invalidateQueries(["dashboard"]);
      smartQueryClient.invalidateQueries([
        "goods_and_services",
        "detail",
        goodsAndServicesItemId,
        "units",
      ]);
      smartQueryClient.setQueryData(
        ["goods_and_services", "detail", goodsAndServicesItemId, "details"],
        goodsAndServicesItem
      );
    },
    onMutate: ({ goodsAndServicesItemId, patch }) => {
      smartQueryClient.setQueryData<GoodsAndServicesItem>(
        ["goods_and_services", "detail", goodsAndServicesItemId, "details"],
        (data) => {
          if (!data) return undefined;
          return { ...data, ...patch };
        }
      );
    },
  });
}

export function useAddGoodsAndServicesItem() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name?: string }) =>
      axios.post<GoodsAndServicesItem>("/goods_and_services", { name }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["goods_and_services", "list"]);
    },
  });
}

export function useDeleteGoodsAndServicesItem() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ goodsAndServicesItemId }: { goodsAndServicesItemId: number }) =>
      axios.delete("/goods_and_services/" + goodsAndServicesItemId),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["goods_and_services"]);
    },
  });
}

export interface GoodsAndServicesItemRelation {
  type: "product_proxy" | "product_source" | "product" | "facility";
  row_id: string;
  id: number;
  name: string;
}

export function useGetGoodsAndServicesItemRelations({
  goodsAndServicesItemId,
  pagination,
  enabled,
}: {
  goodsAndServicesItemId: number;
  pagination?: Pagination;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["goods_and_services", "detail", goodsAndServicesItemId, "relations", pagination],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetData<GoodsAndServicesItemRelation>>(
          "/goods_and_services/" + goodsAndServicesItemId + "/relations",
          {
            params: {
              page: pagination?.page === undefined ? 1 : pagination.page,
              page_size: pagination?.pageSize || 50,
            },
          }
        )
        .then((result) => result.data),
  });
}
export interface GoodsAndServicesItemRelationPerYear {
  year: number | null;
  facility_count: number;
  product_count: number;
}

export function useGetGoodsAndServicesItemRelationsPerYear({
  goodsAndServicesItemId,
  enabled,
}: {
  goodsAndServicesItemId: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["goods_and_services", "detail", goodsAndServicesItemId, "relations", "per_year"],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GoodsAndServicesItemRelationPerYear[]>(
          "/goods_and_services/" + goodsAndServicesItemId + "/relations_per_year"
        )
        .then((result) => result.data),
  });
}

export function useGoodsAndServicesItemStatusPerYear() {
  return useQuery({
    queryKey: ["goods_and_services", "list", "years"],
    keepPreviousData: true,
    queryFn: () =>
      axios.get<YearStatus[]>("/goods_and_services/years", {}).then((result) => result.data),
  });
}
